import { localStorageService } from '$lib/services';
import Emitter from '$lib/services/streaming/eventsDispatcher';
import Events from '$lib/services/streaming/eventsDispatcher/events';

import { createManualLock } from '@components/utils/lock';

export default class MediaStreamController {
    mediaStream: MediaStream | null;
    isVideoOn: boolean;
    isAudioOn: boolean;
    videoTrack: MediaStreamTrack | null;
    audioTrack: MediaStreamTrack | null;
    mediaStreamLock = createManualLock();

    constructor() {
        this.mediaStream = null;
        this.isVideoOn = false;
        this.isAudioOn = false;
        this.videoTrack = null;
        this.audioTrack = null;
    }

    public async getAvailableDevices() {
        return navigator.mediaDevices.enumerateDevices();
    }

    public async getUserMedia(videoDeviceId?: string | null, audioDeviceId?: string | null) {
        return navigator.mediaDevices.getUserMedia({
            video: videoDeviceId ? { deviceId: videoDeviceId } : true,
            audio: audioDeviceId ? { deviceId: audioDeviceId } : true,
        });
    }

    public async getMediaStream(videoDeviceId?: string | null, audioDeviceId?: string | null) {
        try {
            if (this.mediaStream) {
                await this.stopMediaStream();
            }
            this.mediaStreamLock.lock();
            this.mediaStream = await this.getUserMedia(videoDeviceId, audioDeviceId);
            this.mediaStreamLock.unlock();
            this.videoTrack = this.mediaStream.getVideoTracks()?.[0];
            this.audioTrack = this.mediaStream.getAudioTracks()?.[0];

            this.isAudioOn = localStorageService.getItem(
                'microphone',
                this.isAudioOn || this.audioTrack.enabled
            );
            this.isVideoOn = localStorageService.getItem(
                'camera',
                this.isVideoOn || this.videoTrack.enabled
            );

            Emitter.emit(Events.MEDIA_STREAM_READY);
        } catch (e) {
            console.log(e, 'error');

            this.mediaStream = null;
        }

        return this.mediaStream;
    }

    resetMediaStream() {
        this.isAudioOn = false;
        this.isVideoOn = false;
        localStorageService.setItem('camera', Boolean(this.isVideoOn));
        localStorageService.setItem('microphone', Boolean(this.isAudioOn));

        Emitter.emit(Events.MEDIA_STREAM_STATE_UPDATE);
    }

    public toggleStreamVideo() {
        this.setStreamVideoStatus(!this.isVideoOn);
    }

    public setStreamVideoStatus(enabled: boolean) {
        if (this.mediaStream && this.videoTrack) {
            this.isVideoOn = enabled;
            this.videoTrack.enabled = this.isVideoOn;
        }

        localStorageService.setItem('camera', Boolean(this.isVideoOn));
    }

    public toggleStreamAudio() {
        if (this.mediaStream && this.audioTrack) {
            this.isAudioOn = !this.isAudioOn;
            this.audioTrack.enabled = this.isAudioOn;
        }

        localStorageService.setItem('microphone', Boolean(this.isAudioOn));
    }

    public setStreamAudioStatus(enabled: boolean) {
        if (this.mediaStream && this.audioTrack) {
            this.isAudioOn = enabled;
            this.audioTrack.enabled = this.isAudioOn;
        }

        localStorageService.setItem('microphone', Boolean(this.isAudioOn));
    }

    public async stopMediaStream() {
        await this.mediaStreamLock.wait();
        this.mediaStream?.getTracks().forEach((track) => {
            track.stop();
        });
    }

    public async subscribeOnMediaStreamStatus(
        onCameraPermissionChangedCallback?: (hasPermission: boolean) => void,
        onMicPermissionChangedCallback?: (hasPermission: boolean) => void
    ) {
        const navigatorPermissions = window.navigator.permissions;
        const cameraPermission = { name: 'camera' as PermissionName };
        const microphonePermission = { name: 'microphone' as PermissionName };

        try {
            // хак для реагирования на изменение разрешений на камере
            const cameraStatus = await navigatorPermissions.query(cameraPermission);
            const microphoneStatus = await navigatorPermissions.query(microphonePermission);

            let currentCameraPermission = cameraStatus.state === 'granted' ? true : false;

            const cameraStatusChangedCallback = () => {
                if (onCameraPermissionChangedCallback) {
                    const newState = cameraStatus.state === 'granted' ? true : false;
                    if (currentCameraPermission !== newState) {
                        currentCameraPermission = newState;
                        onCameraPermissionChangedCallback(newState);
                    }
                }
            };
            cameraStatus.addEventListener('change', cameraStatusChangedCallback);

            const microphoneStatusChangedCallback = () => {
                if (onMicPermissionChangedCallback) {
                    onMicPermissionChangedCallback(microphoneStatus.state === 'granted' ? true : false);
                }
            };
            microphoneStatus.addEventListener('change', microphoneStatusChangedCallback);

            return () => {
                cameraStatus.removeEventListener('change', cameraStatusChangedCallback);
                microphoneStatus.removeEventListener('change', microphoneStatusChangedCallback);
            };
        } catch (e) {
            return () => { /* @noop */ };
            /* @noop */
            // Experimental feature - ignore errors
        }
    }
}
